import { FileSearchOutlined } from '@ant-design/icons';
import EditOutlined from '@ant-design/icons/EditOutlined';
import CheckCircleOutlined from '@ant-design/icons/lib/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/lib/icons/CloseCircleOutlined';
import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';
import Table from 'antd/lib/table';
import { ColumnsType } from 'antd/lib/table/interface';
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { EmailUtilityAutomationFormModal } from '../../components/email-utility-automation/EmailUtilityAutomationFormModal';
import { EmailUtilityAutomationHistoryModal } from '../../components/email-utility-automation/EmailUtilityAutomationHistoryModal';
import { EmailUtilityAutomationShortHistoryItem } from '../../components/email-utility-automation/EmailUtilityAutomationShortHistoryItem';
import { filterEmailUtilityAutomationsBySearch } from '../../components/email-utility-automation/helpers';
import { AuditTrailModal } from '../../components/modals/AuditTrailModal/AuditTrailModal';
import { PageHeader } from '../../components/PageHeader';
import { ProgramTag } from '../../components/ProgramTag';
import { SearchInput } from '../../components/searchInput/searchInput';
import { ActionList } from '../../components/table/actionList/actionList';
import { WithEmptyDataTable } from '../../components/table/withEmptyDataTable';
import { useDocumentTitle } from '../../components/useDocumentTitle';
import { AuditEntityType } from '../../domain/audit-trail/interface';
import { PAGE_ACTION } from '../../domain/commonConst';
import { IEmailUtilityAutomation } from '../../domain/email-utility-automation/interface';
import { useEmailUtilityAutomationListQuery } from '../../domain/email-utility-automation/queries';
import { ERROR_COLOR } from '../../theme';
import { toUsefulParams } from '../toUsefulParams';
import { usePageLocation } from '../usePageState';

interface IPageState {
    filter?: {};
    search?: string;
    pagination?: {
        pageSize: number;
        current: number;
    };
    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export const EmailUtilityAutomations: React.FC = () => {
    useDocumentTitle('Utility Automations');

    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation({ parseNumbers: false });
    const pageState: IPageState = queryToState(location.search);

    const { data, isLoading, refetch } = useEmailUtilityAutomationListQuery({});
    const emailUtilityAutomations = useMemo(() => filterEmailUtilityAutomationsBySearch(data || [], pageState.search), [data, pageState.search]);

    const onSearch = (value: string) => {
        setPageQuery({ ...pageState, search: value });
    };

    const onTableChange = (pagination: any, filter: any, sorter: any) => {
        const preparedParams = toUsefulParams({ pagination, filter, sorter });

        setPageQuery({ ...pageState, ...preparedParams });
    };

    const [emailUtilityAutomation, setEmailUtilityAutomation] = useState<IEmailUtilityAutomation | null>(null);
    const [pageAction, setPageAction] = useState<PAGE_ACTION | null>(null);

    const onEditModalOpen = (automation: IEmailUtilityAutomation) => () => {
        setEmailUtilityAutomation(automation);
        setPageAction(PAGE_ACTION.NEW_EDIT);
    };

    const onHistoryModalOpen = (automation: IEmailUtilityAutomation) => () => {
        setEmailUtilityAutomation(automation);
        setPageAction(PAGE_ACTION.SHOW_LOGS);
    };

    const onAuditTrailModalOpen = (automation: IEmailUtilityAutomation) => () => {
        setEmailUtilityAutomation(automation);
        setPageAction(PAGE_ACTION.AUDIT_TRAIL);
    };

    const onModalClose = async (needRefresh?: boolean) => {
        setEmailUtilityAutomation(null);
        setPageAction(null);

        if (needRefresh) {
            await refetch();
        }
    };

    const columns: ColumnsType<IEmailUtilityAutomation> = [
        {
            title: 'Utility Automation',
            key: 'title',
            dataIndex: 'title',
        },
        {
            title: 'Received From',
            key: 'receivedFrom',
            dataIndex: 'receivedFrom',
            render: (_, record) => {
                return record.receiving_from.join(', ');
            },
        },
        {
            title: 'Active',
            key: 'is_active',
            dataIndex: 'is_active',
            render: (_, record) => {
                return record.is_active ? (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px', marginLeft: '12px' }} />
                ) : (
                    <CloseCircleOutlined style={{ color: ERROR_COLOR, fontSize: '18px', marginLeft: '12px' }} />
                );
            },
        },
        {
            title: 'Programs',
            key: 'programs',
            dataIndex: 'programs',
            render: (_, record) => {
                return record.programs?.map((program, index) => {
                    return <ProgramTag programName={program.name} key={`program-${index}`} />;
                });
            },
        },
        {
            title: 'Last 7 Days',
            key: 'history',
            dataIndex: 'history',
            render: (_, record) => {
                if (record.history.length === 0) return 'No received emails';

                return (
                    <span>
                        {record.history.map((historyItem, index) => (
                            <EmailUtilityAutomationShortHistoryItem key={index} historyItem={historyItem} />
                        ))}
                    </span>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                return (
                    <ActionList
                        alwaysShow
                        item={record}
                        actions={[
                            {
                                title: 'View Parser History',
                                onClick: () => onHistoryModalOpen(record),
                                icon: <FileSearchOutlined />,
                            },
                            {
                                title: 'Edit',
                                onClick: () => onEditModalOpen(record),
                                icon: <EditOutlined />,
                            },
                            {
                                title: 'Audit Trail',
                                icon: <HistoryOutlined />,
                                onClick: () => onAuditTrailModalOpen(record),
                            },
                        ]}
                    />
                );
            },
        },
    ];

    return (
        <>
            <PageHeader
                pageTitle="Utility Automations"
                actions={[<SearchInput onSearch={onSearch} defaultValue={pageState?.search} key="search-program" />]}
            />
            <WithEmptyDataTable condition={isLoading}>
                <Table
                    size="small"
                    rowKey="automationKey"
                    columns={columns}
                    sticky={{ offsetHeader: 62 }}
                    dataSource={emailUtilityAutomations}
                    loading={isLoading}
                    onChange={onTableChange}
                    pagination={{
                        pageSize: pageState.pagination?.pageSize || 10,
                        current: pageState.pagination?.current || 1,
                        showSizeChanger: true,
                        size: 'default',
                    }}
                />
            </WithEmptyDataTable>

            {emailUtilityAutomation && pageAction === PAGE_ACTION.NEW_EDIT && (
                <EmailUtilityAutomationFormModal utilityAutomation={emailUtilityAutomation} onClose={onModalClose} />
            )}

            {emailUtilityAutomation && pageAction === PAGE_ACTION.SHOW_LOGS && (
                <EmailUtilityAutomationHistoryModal utilityAutomation={emailUtilityAutomation} onClose={onModalClose} />
            )}

            {emailUtilityAutomation && pageAction === PAGE_ACTION.AUDIT_TRAIL && (
                <AuditTrailModal
                    entityId={emailUtilityAutomation.automation_key}
                    entityType={AuditEntityType.EMAIL_UTILITY_AUTOMATION}
                    onClose={onModalClose}
                />
            )}
        </>
    );
};
